import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router';
import { qk } from '@/api/queries';
import type { Website } from '@/api/types';
import { paths } from '@/app/paths';
import { Button, Card, Icon, useEscapeLayer, useToast } from '@/design-system';
import { activeKey } from '@/features/variants/VariantsTab';
import { applyVariantState, landingPalette, VARIANT_KEYS, variantsKey, variantsQuery, type VariantOut } from '@/features/variants/api';
import { errorText } from '@/shell/errorText';
import { LoadError } from '@/shell/LoadError';
import { approveVariant, approvedProductsQuery, moveHead, previewUrlQuery, revisionsKey, themeQuery, type RevisionOut } from './api';
import type { Device } from './bridge';
import { ChatPanel } from './ChatPanel';
import { ComparePane } from './ComparePane';
import { PreviewFrame } from './PreviewFrame';
import { SidePanel } from './SidePanel';
import { StandInPreview, type Selection } from './StandInPreview';
import s from './designer.module.css';

const DEVICES: Device[] = ['desktop', 'tablet', 'mobile'];

/** What the designer's panels share (the chat, sections and versions use it, P4-E03). */
export type DesignerCtx = {
  site: Website;
  variant: VariantOut;
  selection: Selection;
  select: (sel: Selection) => void;
};

/**
 * /sites/:id/designer — «AI Designer» (DESIGN_SPEC §4.5): the toolbar (A / B / C, Desktop / Tablet /
 * Mobile, «Выделить», undo and redo, the revision, «Утвердить», full screen) over the variant's
 * preview. Normal layout: the preview on a full row, the chat and the side column below; full screen:
 * the chat on the left, the preview on the right, Esc leaves.
 */
export function DesignerTab() {
  const site = useOutletContext<Website>();
  const { t } = useTranslation('designer');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = useQuery(variantsQuery(site.id));
  const [selection, select] = useState<Selection>({ section: 'hero', el: null });
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  const ready = q.data.filter((v) => v.status === 'ready');
  const wanted = params.get('variant') ?? activeKey(site);
  const variant = ready.find((v) => v.key === wanted) ?? ready[0];
  if (!variant)
    return (
      <Card className={s.empty}>
        <Icon name="sparkles" size={24} className={s.emptyIcon} />
        <h2 className={s.emptyTitle}>{t('empty.title')}</h2>
        <span className={s.emptyText}>{t('empty.text')}</span>
        <div>
          <Button variant="secondary" size={36} icon="copy" iconSize={14} onClick={() => navigate(paths.site(site.id, 'variants'))}>
            {t('empty.to_variants')}
          </Button>
        </div>
      </Card>
    );
  const ctx: DesignerCtx = { site, variant, selection, select };
  return (
    <Designer
      ctx={ctx}
      readyKeys={ready.map((v) => v.key)}
      onVariant={(k) =>
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('variant', k);
            return next;
          },
          { replace: true },
        )
      }
    />
  );
}

type Props = {
  ctx: DesignerCtx;
  readyKeys: string[];
  onVariant: (key: string) => void;
};

function Designer({ ctx, readyKeys, onVariant }: Props) {
  const { site, variant, selection, select } = ctx;
  const { t } = useTranslation('designer');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [device, setDevice] = useState<Device>('desktop');
  const [pick, setPick] = useState(true);
  const [fs, setFs] = useState(false);
  const [compare, setCompare] = useState<RevisionOut | null>(null);
  useEscapeLayer(fs, () => setFs(false));
  const head = variant.head;
  // The variant carries its head's preview; a head that moved since asks for its own.
  const own = variant.preview && variant.preview.revision_id === head?.id ? variant.preview : null;
  const fetched = useQuery({ ...previewUrlQuery(head?.id ?? ''), enabled: !!head && !own }).data;
  const previewUrl = (own ?? fetched)?.preview_url ?? null;
  const products = useQuery(approvedProductsQuery(site.organization.id)).data;
  const standInProducts = (products ?? []).map((p) => ({ name: String(p.data.name ?? ''), price: p.data.price === null || p.data.price === undefined ? null : Number(p.data.price) }));

  const move = (dir: 'undo' | 'redo') =>
    moveHead(variant.id, dir)
      .then((state) => {
        applyVariantState(qc, site.id, state);
        void qc.invalidateQueries({ queryKey: variantsKey(site.id) });
        void qc.invalidateQueries({ queryKey: revisionsKey(variant.id) });
      })
      .catch((e: unknown) => toast(errorText(e, tc)));
  const approve = () =>
    // The revision the PM is looking at (the head); the answer carries the extracted theme.json.
    approveVariant(variant.id, variant.head?.id)
      .then((out) => {
        qc.setQueryData(themeQuery(site.id).queryKey, out.theme);
        void qc.invalidateQueries({ queryKey: qk.website(site.id) });
        toast(t('approved', { key: variant.key }));
        navigate(paths.site(site.id, 'sync'));
      })
      .catch((e: unknown) => toast(errorText(e, tc)));

  const label = t('preview.label', { key: variant.key });
  return (
    <div className={s.layout} data-fs={fs}>
      <ChatPanel ctx={ctx} fullscreen={fs} />
      <section className={s.previewCard}>
        <div className={s.toolbar}>
          <div className={s.seg} role="radiogroup" aria-label={t('toolbar.variants')}>
            {VARIANT_KEYS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={variant.key === k} className={s.keyBtn} disabled={!readyKeys.includes(k)} onClick={() => onVariant(k)}>
                {k}
              </button>
            ))}
          </div>
          <div className={s.seg} role="radiogroup" aria-label={t('toolbar.device')}>
            {DEVICES.map((d) => (
              <button key={d} type="button" role="radio" aria-checked={device === d} className={s.segBtn} onClick={() => setDevice(d)}>
                {t(`toolbar.devices.${d}`)}
              </button>
            ))}
          </div>
          <button type="button" className={s.pick} aria-pressed={pick} onClick={() => setPick((v) => !v)}>
            <Icon name="mouse-pointer-click" size={14} />
            {t('toolbar.pick')}
          </button>
          <div className={s.right}>
            <button type="button" className={s.circle} title={t('toolbar.undo')} aria-label={t('toolbar.undo')} disabled={!variant.can_undo} onClick={() => void move('undo')}>
              <Icon name="undo-2" size={14} />
            </button>
            <button type="button" className={s.circle} title={t('toolbar.redo')} aria-label={t('toolbar.redo')} disabled={!variant.can_redo} onClick={() => void move('redo')}>
              <Icon name="redo-2" size={14} />
            </button>
            <span className={s.revLabel}>{head ? t('toolbar.rev', { key: variant.key, n: head.number }) : variant.key}</span>
            <button type="button" className={s.approve} onClick={() => void approve()}>
              <Icon name="check" size={14} />
              {t('toolbar.approve')}
            </button>
            <button type="button" className={s.fsBtn} title={fs ? t('toolbar.fs_off') : t('toolbar.fs_on')} aria-label={fs ? t('toolbar.fs_off') : t('toolbar.fs_on')} aria-pressed={fs} onClick={() => setFs((v) => !v)}>
              <Icon name={fs ? 'minimize-2' : 'maximize-2'} size={15} />
            </button>
          </div>
        </div>
        <div className={s.stage}>
          {compare && head ? (
            <ComparePane ctx={ctx} from={compare} to={head} device={device} products={standInProducts} onClose={() => setCompare(null)} />
          ) : previewUrl ? (
            <PreviewFrame url={previewUrl} device={device} pick={pick} selection={selection} onSelect={(sel) => select(sel)} label={t('preview.frame')} />
          ) : (
            <StandInPreview
              palette={landingPalette(variant)}
              brand={site.organization.brand_name}
              headline={t('stand_in.headline')}
              products={standInProducts}
              device={device}
              pick={pick}
              selection={selection}
              onSelect={select}
              label={label}
            />
          )}
        </div>
      </section>
      <SidePanel ctx={ctx} onCompare={setCompare} />
    </div>
  );
}
